import { useMemo, useState } from 'react';
import { useColaboradores } from '@/features/colaboradores/hooks/useColaboradores';
import { ColaboradorFormModal } from '@/features/colaboradores/pages/ColaboradorFormModal';
import { colaboradoresApi } from '@/features/colaboradores/api/colaboradoresApi';
import type { ColaboradorResumo } from '@/features/colaboradores/api/colaboradoresApi';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { IconEdit, IconLock, IconRefresh, IconTrash, IconUsers } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { Field } from '@/shared/ui/Field';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';

type ModoForm =
  | { tipo: 'fechado' }
  | { tipo: 'novo' }
  | { tipo: 'editar'; colaborador: ColaboradorResumo };

export function ColaboradoresPage() {
  const { colaboradores, carregando, erro, recarregar, atualizar, excluir } = useColaboradores();
  // Criar e editar têm endereço próprio (`/colaboradores/novo`,
  // `/colaboradores/:id/editar`).
  const rota = useRotaEntidade(ROTAS.colaboradores);
  const [busca, setBusca] = useState('');
  // PIN de caixa do PDV: define ou remove. Guardamos só o hash, então
  // não há como exibir o PIN atual — se esqueceu, cadastra outro.
  const [definindoPin, setDefinindoPin] = useState<ColaboradorResumo | null>(null);
  const dialogos = useDialogos();

  const modo = useMemo<ModoForm>(() => {
    if (rota.modo === 'novo') return { tipo: 'novo' };
    if (rota.modo === 'editar') {
      const registro = colaboradores.find((x) => x.id === rota.id);
      return registro ? { tipo: 'editar', colaborador: registro } : { tipo: 'fechado' };
    }
    return { tipo: 'fechado' };
  }, [rota, colaboradores]);

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (q === '') return colaboradores;
    return colaboradores.filter((c) =>
      normalizarBusca(`${c.nome} ${c.cargo ?? ''} ${c.email ?? ''} ${c.telefone ?? ''}`).includes(q),
    );
  }, [colaboradores, busca]);

  const { pagina, setPagina, paginados, total, totalPaginas, tamanhoPagina } =
    usePaginacao(filtrados, 25);

  async function confirmarExclusao(colaborador: ColaboradorResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir colaborador',
      mensagem: `Excluir o colaborador "${colaborador.nome}"?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await excluir(colaborador.id);
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao excluir.');
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Colaboradores"
        descricao="Equipe responsável por orçamentos, serviços e peças."
        acoes={
          <>
            <Button onClick={() => void recarregar()} disabled={carregando}>
              <IconRefresh className="btn__icon" />
              {carregando ? 'Atualizando...' : 'Atualizar'}
            </Button>
            <Button variante="primary" onClick={() => rota.abrir('novo')}>
              + Novo colaborador
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">
            {filtrados.length} de {colaboradores.length} colaborador(es)
          </span>
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar por nome, cargo, e-mail ou telefone..."
            ariaLabel="Buscar colaborador"
          />
        </div>
        <div className="card__body card__body--flush">
          {colaboradores.length === 0 && !carregando && !erro ? (
            <EmptyState
              icone={<IconUsers />}
              titulo="Nenhum colaborador cadastrado ainda."
              descricao="Cadastre a equipe para vincular responsáveis às ordens de serviço."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconUsers />}
              titulo="Nenhum colaborador corresponde à busca."
              descricao="Tente outro termo ou limpe o campo de busca."
            />
          ) : (
            <div className="table-scroll"><table className="table table--responsive">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Cargo</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>Status</th>
                  <th>PIN do caixa</th>
                  <th style={{ width: 1 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((c) => (
                  <tr key={c.id}>
                    <td className="table__primary">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {c.nome}
                        <BadgeSyncPendente pendente={c.syncPendente} />
                      </span>
                    </td>
                    <td className="table__muted" data-label="Cargo">{c.cargo ?? '-'}</td>
                    <td className="table__muted" data-label="E-mail">{c.email ?? '-'}</td>
                    <td className="table__muted" data-label="Telefone">{c.telefone ?? '-'}</td>
                    <td data-label="Status">
                      <span className={`badge ${c.ativo ? 'badge--ativo' : 'badge--inativo'}`}>
                        {c.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td data-label="PIN do caixa">
                      <span className={`badge ${c.temPin ? 'badge--ativo' : 'badge--inativo'}`}>
                        {c.temPin ? 'Cadastrado' : 'Sem PIN'}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <Button
                          className="btn--icon"
                          aria-label={`Definir PIN de ${c.nome}`}
                          onClick={() => setDefinindoPin(c)}
                        >
                          <IconLock />
                        </Button>
                        <Button
                          className="btn--icon"
                          aria-label={`Editar ${c.nome}`}
                          onClick={() => rota.abrir(`${c.id}/editar`)}
                        >
                          <IconEdit />
                        </Button>
                        <Button
                          className="btn--icon btn--danger"
                          aria-label={`Excluir ${c.nome}`}
                          onClick={() => void confirmarExclusao(c)}
                        >
                          <IconTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            total={total}
            tamanhoPagina={tamanhoPagina}
            onMudarPagina={setPagina}
          />
        </div>
      </div>

      {definindoPin && (
        <ModalPin
          colaborador={definindoPin}
          onFechar={() => setDefinindoPin(null)}
          onSalvo={() => {
            setDefinindoPin(null);
            void recarregar();
          }}
        />
      )}

      {modo.tipo !== 'fechado' && (
        <ColaboradorFormModal
          modo={modo}
          aoFechar={rota.fechar}
          aoSalvar={async (dados) => {
            if (modo.tipo === 'editar') {
              await atualizar(modo.colaborador.id, dados);
            } else {
              await colaboradoresApi.criar(dados);
            }
            rota.fechar();
            await recarregar();
          }}
        />
      )}
    </section>
  );
}

/**
 * Define ou remove o PIN que o colaborador usa para abrir e fechar caixa
 * no PDV. Sem PIN, ele não consegue abrir caixa — é o que dá dono à
 * diferença da gaveta no fim do dia.
 */
function ModalPin({
  colaborador,
  onSalvo,
  onFechar,
}: {
  colaborador: ColaboradorResumo;
  onSalvo: () => void;
  onFechar: () => void;
}) {
  const [pin, setPin] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    if (pin !== confirmacao) {
      setErro('Os dois PINs não são iguais.');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      await colaboradoresApi.definirPin(colaborador.id, pin);
      onSalvo();
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao salvar o PIN.');
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    setSalvando(true);
    try {
      await colaboradoresApi.definirPin(colaborador.id, null);
      onSalvo();
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao remover o PIN.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal aberto titulo={`PIN de ${colaborador.nome}`} onFechar={onFechar}>
      <form className="pdv-form-modal" onSubmit={salvar}>
        <p className="pdv-cartao__ajuda">
          De 4 a 8 dígitos. Guardamos apenas o resultado embaralhado — se ele esquecer, cadastre
          outro.
        </p>

        <Field rotulo="Novo PIN" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input pdv-pin"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              autoFocus
              required
            />
          )}
        </Field>

        <Field rotulo="Repita o PIN" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input pdv-pin"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={8}
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value.replace(/\D/g, ''))}
              required
            />
          )}
        </Field>

        {erro && (
          <p className="pdv-aviso pdv-aviso--erro" role="alert">
            {erro}
          </p>
        )}

        <div className="pdv-form-modal__acoes">
          {colaborador.temPin && (
            <Button
              className="btn--danger"
              onClick={() => void remover()}
              disabled={salvando}
              style={{ marginRight: 'auto' }}
            >
              Remover PIN
            </Button>
          )}
          <Button onClick={onFechar}>Cancelar</Button>
          <Button variante="primary" type="submit" disabled={salvando || pin.length < 4}>
            {salvando ? 'Salvando…' : 'Salvar PIN'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
